import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { aFecha } from "@/components/agente/ChatWhatsApp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { History, Loader2, Pencil, Plus, Power, PowerOff, RefreshCw, Search, UploadCloud } from "lucide-react";
import PreguntaTipicaDialog from "@/components/agente/PreguntaTipicaDialog";
import CargarPreguntasDialog from "@/components/agente/CargarPreguntasDialog";

// PROMPT 132 (2026-10-16): pantalla "Preguntas típicas".
// Las preguntas de siempre de los clientes y cómo contestarlas, ordenadas por tema y por marca. Son las mismas
// entradas de "Qué sabe" (tipo respuesta): lo que se carga acá lo usa el agente igual. Se agregan de a una o de
// a muchas (pegando texto o subiendo un Excel), y la última carga de muchas se puede deshacer entera.

const TODOS = "__todos";
const SIN_TEMA = "__sin_tema";
const GENERALES = "__generales";
const DE_UN_TOUR = "__tour";
const PAGINA = 50;
const CAMPO = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700";

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;
const sinAcentos = (t) => String(t || "").normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
const alfabetico = (a, b) => String(a).localeCompare(String(b), "es", { sensitivity: "base", ignorePunctuation: true });

// "hace 3 min" a partir de la fecha que guarda el servidor (UTC, sin la Z: aFecha la arregla).
function hace(ts) {
  const d = aFecha(ts);
  if (!d) return "";
  const min = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (min < 1) return "hace un momento";
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  return h < 48 ? `hace ${h} h` : `hace ${Math.round(h / 24)} días`;
}

export default function PreguntasTipicasPage() {
  const [datos, setDatos] = useState(null); // GET /whatsapp/preguntas
  const [marcas, setMarcas] = useState([]);
  const [tours, setTours] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [filtroTema, setFiltroTema] = useState(TODOS);
  const [filtroMarca, setFiltroMarca] = useState(TODOS);
  const [vista, setVista] = useState("prendidas"); // prendidas | apagadas | todas
  const [limite, setLimite] = useState(PAGINA);
  const [dialogo, setDialogo] = useState(null); // null | { item } (item vacío = una nueva)
  const [cargarOpen, setCargarOpen] = useState(false);
  const [confirmaDeshacer, setConfirmaDeshacer] = useState(false);
  const [deshaciendo, setDeshaciendo] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [{ data }, ms, ts] = await Promise.all([
        api.get("/whatsapp/preguntas"),
        // Si las marcas o los tours no se pueden leer, la lista igual se ve (con el nombre de la marca o el código del tour).
        api.get("/whatsapp/marcas").then((r) => r.data || []).catch(() => []),
        api.get("/tours").then((r) => (Array.isArray(r.data) ? r.data : r.data?.items || [])).catch(() => []),
      ]);
      setDatos(data);
      setMarcas(ms);
      setTours(ts);
      setError(null);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const items = useMemo(() => datos?.items || [], [datos]);
  const nombreMarca = (slug) => {
    const m = marcas.find((x) => x.slug === slug);
    return m ? m.remitente_nombre || m.nombre || slug : slug;
  };
  const nombreTour = (id) => tours.find((t) => t.id === id)?.nombre || id;

  // Los temas que ya existen, escritos una sola vez (sin importar mayúsculas ni acentos).
  const temas = useMemo(() => {
    const vistos = new Map();
    items.forEach((it) => {
      const t = (it.tema || "").trim();
      if (t && !vistos.has(sinAcentos(t))) vistos.set(sinAcentos(t), t);
    });
    return [...vistos.values()].sort(alfabetico);
  }, [items]);
  const hayDeTour = items.some((it) => it.alcance === "tour");

  const visibles = useMemo(() => {
    const q = sinAcentos(busqueda.trim());
    return items.filter((it) => {
      const prendida = it.activo !== false;
      if (vista === "prendidas" && !prendida) return false;
      if (vista === "apagadas" && prendida) return false;
      if (filtroTema === SIN_TEMA && (it.tema || "").trim()) return false;
      if (filtroTema !== TODOS && filtroTema !== SIN_TEMA && sinAcentos(it.tema) !== sinAcentos(filtroTema)) return false;
      if (filtroMarca === GENERALES && (it.alcance || "siempre") !== "siempre") return false;
      if (filtroMarca === DE_UN_TOUR && it.alcance !== "tour") return false;
      if (filtroMarca.startsWith("m:") && !(it.alcance === "marca" && it.marca_slug === filtroMarca.slice(2))) return false;
      if (!q) return true;
      return sinAcentos(`${it.pregunta || ""} ${it.respuesta || ""} ${it.tema || ""}`).includes(q);
    });
  }, [items, busqueda, filtroTema, filtroMarca, vista]);

  // Agrupadas por tema (los temas en orden, "Sin tema" al final) y, dentro de cada una, por pregunta.
  const grupos = useMemo(() => {
    const mapa = new Map();
    visibles.forEach((it) => {
      const t = (it.tema || "").trim();
      const clave = t ? sinAcentos(t) : "";
      if (!mapa.has(clave)) mapa.set(clave, { clave, tema: t, items: [] });
      mapa.get(clave).items.push(it);
    });
    const lista = [...mapa.values()];
    lista.forEach((g) => g.items.sort((a, b) => alfabetico(a.pregunta || "", b.pregunta || "")));
    lista.sort((a, b) => (a.clave === "" ? 1 : b.clave === "" ? -1 : alfabetico(a.tema, b.tema)));
    return lista;
  }, [visibles]);

  let restantes = limite;
  const gruposMostrados = grupos
    .map((g) => {
      const mostrados = g.items.slice(0, Math.max(0, restantes));
      restantes -= mostrados.length;
      return { ...g, mostrados };
    })
    .filter((g) => g.mostrados.length > 0);

  const apagar = async (it) => {
    try {
      await api.delete(`/whatsapp/conocimiento/${it.id}`);
      toast.success("Apagada: el agente deja de usarla. Podés volver a prenderla desde «Apagadas».");
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const prender = async (it) => {
    try {
      await api.put(`/whatsapp/conocimiento/${it.id}`, { activo: true });
      toast.success("Listo, el agente vuelve a usarla.");
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deshacerUltima = async () => {
    const carga = datos?.ultima_carga;
    if (!carga) return;
    setDeshaciendo(true);
    try {
      const { data } = await api.post(`/whatsapp/preguntas/lote/${carga.lote_id}/deshacer`);
      toast.success(`Listo: se apagaron ${plural(data.apagadas, "pregunta", "preguntas")} de esa carga.`);
      setConfirmaDeshacer(false);
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeshaciendo(false);
    }
  };

  const limites = datos?.limites || {};
  const usadas = datos?.entradas_activas ?? 0;
  const lleno = Boolean(limites.entradas) && usadas >= limites.entradas;
  const casiLleno = Boolean(limites.entradas) && !lleno && usadas >= limites.entradas * 0.9;
  const ultima = datos?.ultima_carga;
  const filtrando = busqueda.trim() || filtroTema !== TODOS || filtroMarca !== TODOS;

  return (
    <div className="space-y-4" data-testid="preguntas-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="sm:max-w-xl">
          <p className="text-sm text-slate-600">
            Las preguntas de siempre de los clientes y cómo contestarlas. Si un cliente pregunta algo parecido, el agente contesta con
            lo que cargues acá, desde la próxima conversación. Cada una vale para todas las marcas o solo para una.
          </p>
          {datos && (
            <p className="text-xs text-slate-400 mt-1" data-testid="preguntas-contadores">
              {plural(datos.activas, "prendida", "prendidas")} · {plural(datos.apagadas, "apagada", "apagadas")} · el agente lee hasta{" "}
              {limites.entradas} entradas ({usadas} en uso, contando las indicaciones de «Qué sabe»). Cuando hay muchas, para cada mensaje
              elige las que más se parecen a lo que preguntó el cliente.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          <Button variant="outline" onClick={cargar} disabled={cargando} data-testid="preguntas-actualizar" className="gap-2">
            <RefreshCw className="h-4 w-4" /> Actualizar
          </Button>
          <Button variant="outline" onClick={() => setDialogo({ item: null })} data-testid="preguntas-agregar" className="gap-2">
            <Plus className="h-4 w-4" /> Agregar una
          </Button>
          <Button onClick={() => setCargarOpen(true)} data-testid="preguntas-cargar-muchas" className="gap-2">
            <UploadCloud className="h-4 w-4" /> Cargar muchas
          </Button>
        </div>
      </div>

      {lleno && (
        <Card className="p-3 border-amber-200 bg-amber-50/60" data-testid="preguntas-lleno">
          <p className="text-sm text-amber-800">
            El agente ya lee el máximo de {limites.entradas} entradas: las que pasen de ahí no las va a ver. Apagá alguna que no sirva
            para hacer lugar.
          </p>
        </Card>
      )}
      {casiLleno && (
        <p className="text-xs text-amber-700" data-testid="preguntas-casi-lleno">
          Quedan {limites.entradas - usadas} lugares de {limites.entradas}.
        </p>
      )}

      {ultima && (
        <Card className="p-3 border-sky-200 bg-sky-50/50" data-testid="preguntas-ultima-carga">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-slate-700 flex items-center gap-1.5">
              <History className="h-4 w-4 text-sky-600" />
              Última carga de muchas: {plural(ultima.cantidad, "pregunta", "preguntas")} {hace(ultima.cuando)}
              {ultima.usuario ? ` · ${ultima.usuario}` : ""}
            </p>
            {confirmaDeshacer ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-600">
                  ¿Apagar {plural(ultima.cantidad, "pregunta", "preguntas")} de esa carga? No se borran.
                </span>
                <Button size="sm" onClick={deshacerUltima} disabled={deshaciendo} data-testid="preguntas-deshacer-confirmar" className="gap-1.5">
                  {deshaciendo ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sí, apagarlas
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmaDeshacer(false)} data-testid="preguntas-deshacer-no">
                  No
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setConfirmaDeshacer(true)} data-testid="preguntas-deshacer">
                Deshacer esa carga
              </Button>
            )}
          </div>
        </Card>
      )}

      <div className="flex flex-col lg:flex-row gap-2 lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              setLimite(PAGINA);
            }}
            placeholder="Buscar en las preguntas y respuestas…"
            className="pl-9"
            data-testid="preguntas-buscar"
          />
        </div>
        <select
          value={filtroTema}
          onChange={(e) => {
            setFiltroTema(e.target.value);
            setLimite(PAGINA);
          }}
          className={CAMPO}
          aria-label="Tema"
          data-testid="preguntas-filtro-tema"
        >
          <option value={TODOS}>Todos los temas</option>
          {temas.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
          <option value={SIN_TEMA}>Sin tema</option>
        </select>
        <select
          value={filtroMarca}
          onChange={(e) => {
            setFiltroMarca(e.target.value);
            setLimite(PAGINA);
          }}
          className={CAMPO}
          aria-label="Marca"
          data-testid="preguntas-filtro-marca"
        >
          <option value={TODOS}>Todas las marcas y generales</option>
          <option value={GENERALES}>Las que valen para todas las marcas</option>
          {marcas.map((m) => (
            <option key={m.slug} value={`m:${m.slug}`}>Solo de {m.remitente_nombre || m.nombre}</option>
          ))}
          {hayDeTour ? <option value={DE_UN_TOUR}>Las de un tour</option> : null}
        </select>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="preguntas-vistas">
        {[
          ["prendidas", "Prendidas", datos?.activas ?? 0],
          ["apagadas", "Apagadas", datos?.apagadas ?? 0],
          ["todas", "Todas", items.length],
        ].map(([clave, texto, n]) => (
          <button
            key={clave}
            type="button"
            onClick={() => {
              setVista(clave);
              setLimite(PAGINA);
            }}
            data-testid={`preguntas-vista-${clave}`}
            className={`rounded-full border px-2.5 py-1 text-xs ${
              vista === clave
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            {texto} ({n})
          </button>
        ))}
      </div>

      {cargando && !datos ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : error && !datos ? (
        <div className="text-center py-10 space-y-2" data-testid="preguntas-error">
          <p className="text-sm text-red-600">{error}</p>
          <Button variant="outline" size="sm" onClick={cargar}>Reintentar</Button>
        </div>
      ) : visibles.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-10" data-testid="preguntas-vacio">
          {items.length === 0
            ? "Todavía no hay preguntas típicas. Agregá una o cargá muchas de una vez."
            : filtrando
              ? "No hay nada con esa búsqueda o esos filtros."
              : vista === "apagadas"
                ? "No hay ninguna apagada."
                : "No hay ninguna prendida."}
        </p>
      ) : (
        <div className="space-y-5">
          {gruposMostrados.map((g) => (
            <section key={g.clave || "sin-tema"} className="space-y-2" data-testid={`preguntas-tema-${g.clave || "sin-tema"}`}>
              <h2 className="text-sm font-semibold text-slate-800 flex items-baseline gap-1.5">
                {g.tema || "Sin tema"}
                <span className="text-xs font-normal text-slate-400">({g.items.length})</span>
              </h2>
              {g.mostrados.map((it) => {
                const apagada = it.activo === false;
                return (
                  <Card key={it.id} className={`p-3 ${apagada ? "bg-slate-50 opacity-80" : ""}`} data-testid={`pregunta-${it.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {it.alcance === "tour" ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-violet-50 text-violet-700 border-violet-200">
                              Tour: {nombreTour(it.tour_id)}
                            </span>
                          ) : it.alcance === "marca" ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-sky-50 text-sky-700 border-sky-200">
                              {nombreMarca(it.marca_slug)}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-slate-100 text-slate-600 border-slate-200">
                              Todas las marcas
                            </span>
                          )}
                          {apagada ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-amber-50 text-amber-700 border-amber-200">
                              Apagada
                            </span>
                          ) : null}
                          {it.usos ? <span className="text-[10px] text-slate-400">se usó {plural(it.usos, "vez", "veces")}</span> : null}
                        </div>
                        <p className="text-sm font-medium text-slate-900 mt-1 break-words">{it.pregunta}</p>
                        <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap break-words">{it.respuesta}</p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-1 shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => setDialogo({ item: it })} data-testid={`pregunta-editar-${it.id}`} className="gap-1.5">
                          <Pencil className="h-4 w-4" /> Editar
                        </Button>
                        {apagada ? (
                          <Button variant="ghost" size="sm" onClick={() => prender(it)} data-testid={`pregunta-prender-${it.id}`} className="gap-1.5">
                            <Power className="h-4 w-4" /> Prender
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => apagar(it)} data-testid={`pregunta-apagar-${it.id}`} className="gap-1.5">
                            <PowerOff className="h-4 w-4" /> Apagar
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </section>
          ))}
          {visibles.length > limite && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => setLimite((n) => n + PAGINA)} data-testid="preguntas-mostrar-mas">
                Mostrar más ({visibles.length - limite} sin ver)
              </Button>
            </div>
          )}
        </div>
      )}

      {dialogo && (
        <PreguntaTipicaDialog
          key={dialogo.item?.id || "nueva"}
          open
          onOpenChange={(v) => {
            if (!v) setDialogo(null);
          }}
          item={dialogo.item}
          marcas={marcas}
          tours={tours}
          temas={temas}
          items={items}
          onGuardado={cargar}
        />
      )}
      {cargarOpen && (
        <CargarPreguntasDialog
          open
          onOpenChange={(v) => {
            if (!v) setCargarOpen(false);
          }}
          marcas={marcas}
          temas={temas}
          onCargado={cargar}
        />
      )}
    </div>
  );
}
